import { isToday } from "./index";
import { format, parseISO } from "date-fns";

export const kFormatter = (num, fraction = 2) => (num > 999 ? `${(num / 1000).toFixed(fraction)}k` : num);

export const formatNumber = value => {
  const num = Number(value);
  if (isNaN(num)) return value;

  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const title = (value, replacer = " ") => {
  if (!value) return "";
  const str = value.toString();

  const arr = str.split(replacer);
  const capitalizedArray = [];
  arr.forEach(word => {
    const capitalized = word.charAt(0).toUpperCase() + word.slice(1);
    capitalizedArray.push(capitalized);
  });

  return capitalizedArray.join(" ");
};

export const avatarText = value => {
  if (!value) return "";
  const nameArray = value.split(" ");

  return nameArray.map(word => word.charAt(0).toUpperCase()).join("");
};

/**
 * Return date and time in ISO format
 * @param {String} dateString date to format
 * example: 2023-11-06 12:39
 **/
export const getFullDateAndTime = dateString => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${minute}`;
};

export const getHoursAndMinutes = dateString => {
  const date = new Date(dateString);
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");

  return `${hour}:${minute}`;
};

/**
 * Format and return date in Humanize format
 * Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 * Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = {
    month: "short",
    day: "numeric",
    year: "numeric",
  },
) => {
  if (!value) return value;

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

/**
 * Return short human friendly month representation of date
 * Can also convert date to only time if date is of today (Better UX)
 * @param {String} value date to format
 * @param {Boolean} toTimeForCurrentDay Shall convert to time if day is today/current
 */
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = {
    month: "short",
    day: "numeric",
  };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = {
      hour: "numeric",
      minute: "numeric",
    };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// Strip all the tags from markup and return plain text
export const filterTags = value => value.replace(/<\/?[^>]+(>|$)/g, "");

/**
 * Formats the date to either MM/DD/YYYY, or DD/MM/YYYY.
 * @param {Boolean} us - Determines if the format of the date should be for US, e.g. MM/DD/YYYY, otherwise it's DD/MM/YYYY
 * @param {String} inputDate
 * @returns String
 */
export const changeDateFormat = (inputDate, us = false, shortenYear = false) => {
  if (!inputDate) return;
  const dateObject = typeof inputDate === "string" ? parseISO(inputDate) : inputDate;

  const formatString = us ? (shortenYear ? "MM/dd/yy" : "MM/dd/yyyy") : shortenYear ? "dd/MM/yy" : "dd/MM/yyyy";

  return format(dateObject, formatString);
};
